import {
  Autocomplete,
  Badge,
  Button,
  Checkbox,
  FileInput,
  Flex,
  Grid,
  Group,
  Modal,
  NumberInput,
  ScrollArea,
  Table,
  Tabs,
  Text,
  Textarea,
  TextInput,
} from '@mantine/core';
import { useForm } from '@mantine/form';
import {
  IconDownload,
  IconFileText,
  IconPlus,
  IconTrash,
  IconUpload,
} from '@tabler/icons';
import { DataTable } from 'mantine-datatable';
import { useEffect, useRef, useState } from 'react';
import * as XLSX from 'xlsx';
import { CustomDateRangePicker } from '../../components/custom-date-range-picker';
import { CustomLoader } from '../../components/custom-loader';
import { Page } from '../../components/page';
import {
  useGenerateGenericCashFlowReceipt,
  useGetCashFlowTransactions,
} from '../../data/hooks/cash-flow';
import { useGetUser } from '../../data/hooks/user';
import { generateGenericCashFlowReceiptRequest } from '../../data/services/cash-flow';
import { formatLocale } from '../../providers/dayjs-plugins';
import {
  errorNotification,
  successNotification,
} from '../../providers/mantine-notifications';
import {
  cashFlowTypeColor,
  cashFlowTypeHumanized,
} from '../../utils/constants/cash-flow';
import { CashFlowTransactionType } from '../../utils/enums/cash-flow';
import { formatBRL, realToUSCash } from '../../utils/helpers';

type FormFilterType = {
  page: number;
  limit: number;
  createdAt: string;
  name: string;
  value: string;
  showCancelled: boolean;
};

type GenericReceiptValueState = {
  value: string;
  description: string;
};

interface BatchRow {
  id: string;
  favoredOrResponsible: string;
  amount: number | '';
  description: string;
}

type PageModalState = 'reward-receipt' | null;

export function RewardCashFlowList() {
  const { loading, fetch, response } = useGetCashFlowTransactions();
  const {
    loading: genCashFlowReceiptLoader,
    fetch: genCashFlowReceiptFetcher,
  } = useGenerateGenericCashFlowReceipt();
  const { fetch: getUsersFetcher, response: getUsersResponse } = useGetUser();

  const [pageModal, setPageModal] = useState<PageModalState>(null);
  const [rewardReceiptValues, setRewardReceiptValues] = useState<
    GenericReceiptValueState[]
  >([{ description: 'INITIAL', value: 'INITIAL' }]);
  const [activeTab, setActiveTab] = useState<'single' | 'batch'>('single');
  const [batchRows, setBatchRows] = useState<BatchRow[]>([
    {
      id: 'initial',
      favoredOrResponsible: '',
      amount: '',
      description: '',
    },
  ]);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [batchLoading, setBatchLoading] = useState(false);
  const page = useRef(1);
  const limit = useRef(10);

  const formFilter = useForm<FormFilterType>({
    initialValues: {
      createdAt: '',
      name: '',
      value: '',
      showCancelled: false,
      limit: limit.current,
      page: page.current,
    },
  });

  const addRewardReceiptForm = useForm({
    initialValues: {
      value: '',
      description: '',
      transactionNote: '',
      username: '',
      total: '0.00',
    },
  });

  function updateTotalValue(value: string) {
    const convertedValue = Number(realToUSCash(value));
    const convertedTotal = Number(addRewardReceiptForm.values.total);
    addRewardReceiptForm.setValues({
      total: (convertedTotal + convertedValue).toFixed(2),
    });
  }

  function addRewardReceiptValue(item: GenericReceiptValueState) {
    if (item.description !== '' && item.value !== '') {
      setRewardReceiptValues([...rewardReceiptValues, item]);
      updateTotalValue(item.value);
    }
    addRewardReceiptForm.setValues({
      description: '',
      value: '',
    });
  }

  function removeRewardReceiptValue(description: string) {
    const cloneList = rewardReceiptValues;
    const findIndex = cloneList.findIndex(
      (item) => item.description === description,
    );

    if (findIndex !== -1) {
      const convertedInverseValue =
        Number(realToUSCash(cloneList[findIndex].value)) * -1;
      updateTotalValue(formatBRL(convertedInverseValue.toFixed(2)));
      cloneList.splice(findIndex, 1);
      setRewardReceiptValues([...cloneList]);
    }
  }

  async function getTransactions(isReset = false) {
    const validatedValues: any = {};

    Object.entries(formFilter.values).forEach(([key, val]) => {
      if (val !== '' && val !== null && val !== undefined) {
        validatedValues[key] = val;
      }
    });

    if (isReset) {
      await fetch({
        params: {
          page: 1,
          limit: 10,
          showCancelled: false,
          type: CashFlowTransactionType.REWARD,
        },
      });
    } else {
      await fetch({
        params: {
          ...validatedValues,
          createdAt:
            validatedValues['createdAt']?.[0] !== null
              ? validatedValues['createdAt']?.join(',')
              : undefined,
          value: validatedValues['value']
            ? realToUSCash(validatedValues['value'])
            : undefined,
          page: page.current,
          limit: limit.current,
          type: CashFlowTransactionType.REWARD,
        },
      });
    }
  }

  async function handlePageChange(newPage: number) {
    page.current = newPage;
    getTransactions();
  }

  async function handlePageLimitChange(newLimit: number) {
    limit.current = newLimit;
    getTransactions();
  }

  async function handleClearFilter() {
    formFilter.reset();
    page.current = 1;
    await getTransactions(true);
  }

  async function getUsers() {
    await getUsersFetcher();
  }

  async function handleCreateRewardReceipt() {
    const clonedList = [...rewardReceiptValues];
    clonedList.splice(0, 1);

    if (clonedList.length === 0) {
      errorNotification({
        title: 'Lista de itens vazia',
        message:
          'Por favor, adicione pelo menos um item com descrição e valor.',
      });
      return;
    }

    await genCashFlowReceiptFetcher({
      data: {
        total: addRewardReceiptForm.values.total,
        username: addRewardReceiptForm.values.username,
        type: CashFlowTransactionType.REWARD,
        transactionNote: addRewardReceiptForm.values.transactionNote || '-',
        list: clonedList.map((item) => ({
          description: item.description,
          value: realToUSCash(item.value),
        })),
      },
      onSuccess: () => {
        setRewardReceiptValues([{ description: 'INITIAL', value: 'INITIAL' }]);
        addRewardReceiptForm.reset();
        setPageModal(null);
        getTransactions();
      },
    });
  }

  const handleAddBatchRow = () => {
    const lastRow = batchRows[batchRows.length - 1];
    setBatchRows((prev) => [
      ...prev,
      {
        id: Math.random().toString(),
        favoredOrResponsible: '',
        amount: '',
        description: lastRow ? lastRow.description : '',
      },
    ]);
  };

  const handleUpdateBatchRow = (
    index: number,
    field: 'favoredOrResponsible' | 'amount' | 'description',
    value: string | number,
  ) => {
    setBatchRows((prev) => {
      const next = [...prev];
      next[index] = { ...next[index], [field]: value } as BatchRow;
      return next;
    });
  };

  const handleRemoveBatchRow = (index: number) => {
    if (batchRows.length === 1) {
      setBatchRows([
        {
          id: 'initial',
          favoredOrResponsible: '',
          amount: '',
          description: '',
        },
      ]);
      return;
    }
    setBatchRows((prev) => prev.filter((_, i) => i !== index));
  };

  const handleDownloadTemplate = () => {
    try {
      const headers = ['Funcionário', 'Descrição', 'Valor'];
      const sampleRow = ['Fulano de Tal', 'Prêmio de Metas - Junho', 150.5];
      const worksheet = XLSX.utils.aoa_to_sheet([headers, sampleRow]);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, 'Modelo de Importação');

      worksheet['!cols'] = [{ wch: 25 }, { wch: 30 }, { wch: 15 }];

      XLSX.writeFile(workbook, 'modelo_importacao_premiacao.xlsx');
    } catch (err) {
      console.error('Erro ao baixar modelo de importação', err);
      errorNotification({
        title: 'Erro ao baixar modelo',
        message: 'Não foi possível gerar o arquivo de modelo.',
      });
    }
  };

  const handleImportExcel = (file: File | null) => {
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const targetResult = e.target?.result;
        if (!targetResult) return;

        const data = new Uint8Array(targetResult as ArrayBuffer);
        const workbook = XLSX.read(data, { type: 'array' });
        const firstSheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[firstSheetName];

        const rows = XLSX.utils.sheet_to_json(worksheet, { header: 1 }) as (
          | string
          | number
          | null
          | undefined
        )[][];

        if (rows.length <= 1) {
          errorNotification({
            title: 'Arquivo vazio',
            message: 'O arquivo importado não contém dados para importar.',
          });
          return;
        }

        const headers = rows[0].map((h) =>
          String(h || '')
            .trim()
            .toLowerCase(),
        );

        const employeeIdx = headers.findIndex(
          (h) =>
            h.includes('funcionário') ||
            h.includes('funcionario') ||
            h.includes('nome'),
        );
        const descIdx = headers.findIndex(
          (h) =>
            h.includes('descrição') ||
            h.includes('descricao') ||
            h.includes('detalhes'),
        );
        const valIdx = headers.findIndex(
          (h) =>
            h.includes('valor') ||
            h.includes('quantia') ||
            h.includes('amount'),
        );

        if (employeeIdx === -1 || valIdx === -1) {
          errorNotification({
            title: 'Colunas não encontradas',
            message:
              "Certifique-se de que o arquivo contém as colunas 'Funcionário' e 'Valor'.",
          });
          return;
        }

        const newRows: BatchRow[] = [];

        for (let i = 1; i < rows.length; i++) {
          const row = rows[i];
          if (!row || row.length === 0) continue;

          const employee = String(row[employeeIdx] || '').trim();
          const description =
            descIdx !== -1 ? String(row[descIdx] || '').trim() : '';
          const rawValue = row[valIdx];

          if (!employee && (rawValue === undefined || rawValue === ''))
            continue;

          let amount: number | '' = '';
          if (rawValue !== undefined && rawValue !== '') {
            if (typeof rawValue === 'number') {
              amount = rawValue;
            } else {
              let cleanVal = String(rawValue)
                .replace(/R\$/g, '')
                .replace(/\s/g, '');

              if (cleanVal.includes(',') && cleanVal.includes('.')) {
                cleanVal = cleanVal.replace(/\./g, '').replace(/,/g, '.');
              } else if (cleanVal.includes(',')) {
                cleanVal = cleanVal.replace(/,/g, '.');
              }
              const parsed = Number(cleanVal);
              amount = isNaN(parsed) ? '' : parsed;
            }
          }

          newRows.push({
            id: Math.random().toString(),
            favoredOrResponsible: employee,
            amount: amount,
            description: description,
          });
        }

        if (newRows.length === 0) {
          errorNotification({
            title: 'Nenhum dado válido',
            message: 'Não encontramos linhas com dados válidos no arquivo.',
          });
          return;
        }

        setBatchRows((prev) => {
          const isFirstRowEmpty =
            prev.length === 1 &&
            !prev[0].favoredOrResponsible &&
            prev[0].amount === '' &&
            !prev[0].description;

          if (isFirstRowEmpty) {
            return newRows;
          }
          return [...prev, ...newRows];
        });

        successNotification({
          title: 'Importação concluída',
          message: `${newRows.length} linha(s) importada(s) com sucesso.`,
        });
      } catch (err) {
        console.error('Erro ao ler arquivo excel', err);
        errorNotification({
          title: 'Erro na leitura',
          message: 'Ocorreu um erro ao processar o arquivo Excel.',
        });
      }
    };

    reader.readAsArrayBuffer(file);
  };

  const handleBatchSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const validRows = batchRows.filter(
      (row) =>
        row.favoredOrResponsible.trim() ||
        row.amount !== '' ||
        row.description.trim(),
    );

    if (validRows.length === 0) {
      errorNotification({
        title: 'Lote vazio',
        message: 'Por favor, preencha pelo menos um lançamento.',
      });
      return;
    }

    const invalidRows = validRows.filter(
      (row) =>
        !row.favoredOrResponsible.trim() ||
        row.amount === '' ||
        Number(row.amount) <= 0,
    );

    if (invalidRows.length > 0) {
      errorNotification({
        title: 'Campos obrigatórios pendentes',
        message:
          'Certifique-se de que todas as linhas preenchidas possuem Funcionário e Valor válido (> 0).',
      });
      return;
    }

    setBatchLoading(true);

    try {
      await Promise.all(
        validRows.map((row) =>
          generateGenericCashFlowReceiptRequest({
            total: Number(row.amount).toFixed(2),
            username: row.favoredOrResponsible.trim(),
            type: CashFlowTransactionType.REWARD,
            transactionNote: row.description.trim() || 'Premiação',
            list: [
              {
                description: row.description.trim() || 'Premiação',
                value: Number(row.amount).toFixed(2),
              },
            ],
          }),
        ),
      );

      successNotification({
        title: 'Sucesso',
        message: `${validRows.length} lançamentos enviados com sucesso!`,
      });

      // Reset state and close modal
      setBatchRows([
        {
          id: 'initial',
          favoredOrResponsible: '',
          amount: '',
          description: '',
        },
      ]);
      setSelectedFile(null);
      setPageModal(null);
      getTransactions();
    } catch (err) {
      console.error(err);
      errorNotification({
        title: 'Erro ao lançar lote',
        message: 'Erro ao registrar lançamentos em lote.',
      });
    } finally {
      setBatchLoading(false);
    }
  };

  useEffect(() => {
    getTransactions();
    getUsers();
  }, []);

  return (
    <Page title="Movimentação de Premiações">
      <CustomLoader loading={loading || genCashFlowReceiptLoader} />
      <Flex m={16} direction="column">
        <Flex align="center" justify="end">
          <Button
            color="ltpBlue.7"
            leftIcon={<IconFileText size={16} />}
            onClick={() => setPageModal('reward-receipt')}
          >
            Lançar Premiação
          </Button>
        </Flex>
        <Flex wrap="wrap" align="center" justify="start" mb={8}>
          <form
            onSubmit={formFilter.onSubmit(() => {
              getTransactions();
            })}
          >
            <Flex align="center" wrap="wrap">
              <CustomDateRangePicker
                required
                withAsterisk
                placeholder="selecione um intervalo"
                label="Intervalo"
                maw={170}
                mb={16}
                mr={8}
                name="createdAt"
                {...formFilter.getInputProps('createdAt')}
              />
              <Autocomplete
                label="Usuário"
                placeholder="comece a digitar um nome"
                data={getUsersResponse?.map((item) => item.username) ?? []}
                mb={16}
                mr={8}
                {...formFilter.getInputProps('name')}
              />
              <TextInput
                label="Valor"
                placeholder="valor"
                type="text"
                mb={16}
                mr={8}
                value={formFilter.values.value}
                onChange={(e) =>
                  formFilter.setFieldValue('value', formatBRL(e.target.value))
                }
              />
              <Button mb={-9} color="ltpBlue.9" onClick={handleClearFilter}>
                Limpar
              </Button>
              <Button mb={-9} ml={16} type="submit">
                Filtrar
              </Button>
              <Checkbox
                label="Cancelados"
                name="showCancelled"
                color="blue"
                ml={8}
                mb={-5}
                checked={formFilter.values.showCancelled}
                {...formFilter.getInputProps('showCancelled')}
              />
            </Flex>
          </form>
        </Flex>
        <DataTable
          recordsPerPage={response?.meta.itemsPerPage ?? 5}
          recordsPerPageOptions={[10, 50, 100, 500]}
          onRecordsPerPageChange={(recordRange) =>
            handlePageLimitChange(recordRange)
          }
          onPageChange={handlePageChange}
          totalRecords={response?.meta.totalItems}
          page={page.current}
          fetching={loading}
          height="72vh"
          noRecordsText="Sem movimentação"
          withBorder
          borderRadius="sm"
          striped
          highlightOnHover
          records={response?.items}
          rowStyle={({ cancelledAt }) =>
            cancelledAt && {
              backgroundColor: 'rgba(255, 0, 0, 0.055)',
              color: 'red',
            }
          }
          columns={[
            {
              accessor: 'username',
              title: 'Funcionário',
            },
            {
              accessor: 'type',
              title: 'Tipo',
              render: ({ type }) => (
                <Badge color={cashFlowTypeColor[type]}>
                  {cashFlowTypeHumanized[type]}
                </Badge>
              ),
            },
            {
              accessor: 'status',
              title: '',
              render: ({ cancelledAt }) =>
                cancelledAt && <Badge color="red.7">Cancelado</Badge>,
            },
            {
              accessor: 'value',
              title: 'Valor',
              render: ({ value, cancelledAt }) => (
                <Text
                  fw="bold"
                  color={Number(value) < 0 ? 'red.7' : 'green.7'}
                  style={{ textDecoration: cancelledAt && 'line-through' }}
                >{`${formatBRL(String(value))}`}</Text>
              ),
            },
            {
              accessor: 'name',
              title: 'Responsável/Solicitante',
            },
            {
              accessor: 'description',
              title: 'Descrição',
            },
            {
              accessor: 'createdAt',
              title: 'Data da Operação',
              render: ({ createdAt }) =>
                createdAt ? formatLocale(createdAt, 'DD/MM/YYYY HH:mm') : '--',
            },
            {
              accessor: 'createdAt',
              title: 'Cancelado em',
              render: ({ cancelledAt }) =>
                cancelledAt
                  ? formatLocale(cancelledAt, 'DD/MM/YYYY HH:mm')
                  : '--',
            },
            {
              accessor: 'createdAt',
              title: 'Cancelado por',
              render: ({ cancelledBy }) => cancelledBy,
            },
          ]}
        />
      </Flex>

      <Modal
        size={activeTab === 'batch' ? '80%' : '60%'}
        title="Lançar Premiação"
        opened={pageModal === 'reward-receipt'}
        onClose={() => {
          setPageModal(null);
          addRewardReceiptForm.reset();
          setRewardReceiptValues([
            { description: 'INITIAL', value: 'INITIAL' },
          ]);
          setBatchRows([
            {
              id: 'initial',
              favoredOrResponsible: '',
              amount: '',
              description: '',
            },
          ]);
          setSelectedFile(null);
          setActiveTab('single');
        }}
        closeOnClickOutside={false}
        closeOnEscape={false}
      >
        <Tabs value={activeTab} onTabChange={(v) => setActiveTab(v as any)}>
          <Tabs.List mb="md">
            <Tabs.Tab value="single">Lançamento Individual</Tabs.Tab>
            <Tabs.Tab value="batch">Lançamento em Lote</Tabs.Tab>
          </Tabs.List>

          <Tabs.Panel value="single">
            <form
              onSubmit={addRewardReceiptForm.onSubmit(
                handleCreateRewardReceipt,
              )}
            >
              <Grid columns={2}>
                <Grid.Col span={1}>
                  <Autocomplete
                    required
                    withAsterisk
                    label="Funcionário"
                    placeholder="comece a digitar um nome"
                    data={getUsersResponse?.map((item) => item.username) ?? []}
                    {...addRewardReceiptForm.getInputProps('username')}
                  />
                </Grid.Col>
                <Grid.Col span={1}>
                  <Text>Total</Text>
                  <Text size={28} fw="bold">
                    {formatBRL(addRewardReceiptForm.values.total)}
                  </Text>
                </Grid.Col>
                <Grid.Col span={2}>
                  <DataTable
                    minHeight={200}
                    noRecordsText="Sem valores"
                    withBorder
                    borderRadius="sm"
                    striped
                    highlightOnHover
                    records={rewardReceiptValues}
                    columns={[
                      {
                        accessor: 'description',
                        title: 'Descrição',
                        render: ({ description }) =>
                          description === 'INITIAL' ? (
                            <TextInput
                              placeholder="descrição"
                              type="text"
                              name="description"
                              {...addRewardReceiptForm.getInputProps(
                                'description',
                              )}
                            />
                          ) : (
                            description
                          ),
                      },
                      {
                        accessor: 'value',
                        title: 'Valor',
                        render: ({ value }) =>
                          value === 'INITIAL' ? (
                            <TextInput
                              placeholder="valor"
                              type="text"
                              name="value"
                              value={addRewardReceiptForm.values.value}
                              onChange={(e) =>
                                addRewardReceiptForm.setFieldValue(
                                  'value',
                                  formatBRL(e.target.value),
                                )
                              }
                            />
                          ) : (
                            value
                          ),
                        width: 120,
                      },
                      {
                        accessor: 'action',
                        title: '',
                        render: ({ description }) =>
                          description === 'INITIAL' ? (
                            <Button
                              type="button"
                              color="blue"
                              variant="light"
                              p={10}
                              onClick={() =>
                                addRewardReceiptValue(
                                  addRewardReceiptForm.values,
                                )
                              }
                            >
                              <IconPlus size={15} />
                            </Button>
                          ) : (
                            <Button
                              type="button"
                              color="red"
                              variant="light"
                              p={10}
                              onClick={() =>
                                removeRewardReceiptValue(description)
                              }
                            >
                              <IconTrash size={15} />
                            </Button>
                          ),
                      },
                    ]}
                  />
                </Grid.Col>
                <Grid.Col span={2}>
                  <Textarea
                    maxLength={255}
                    autosize
                    label="Detalhes"
                    placeholder="obs ou detalhes sobre a premiação"
                    {...addRewardReceiptForm.getInputProps('transactionNote')}
                  />
                </Grid.Col>
              </Grid>
              <Group mt={16} mb={16} position="right">
                <Button type="submit">Lançar Premiação</Button>
              </Group>
            </form>
          </Tabs.Panel>

          <Tabs.Panel value="batch">
            <form onSubmit={handleBatchSubmit}>
              <Grid columns={2} gutter="md">
                <Grid.Col span={2}>
                  <Group position="apart" align="center">
                    <Group>
                      <FileInput
                        placeholder="Importar Excel (.xlsx)"
                        accept=".xlsx"
                        icon={<IconUpload size={14} />}
                        value={selectedFile}
                        onChange={(file) => {
                          setSelectedFile(file);
                          handleImportExcel(file);
                        }}
                        style={{ width: 220 }}
                      />
                      <Button
                        variant="outline"
                        leftIcon={<IconDownload size={14} />}
                        onClick={handleDownloadTemplate}
                      >
                        Baixar Modelo
                      </Button>
                    </Group>

                    <Text size="sm" color="dimmed">
                      Total em Lote:{' '}
                      <Text span fw="bold" size="lg" color="blue">
                        {formatBRL(
                          batchRows
                            .reduce(
                              (sum, row) => sum + Number(row.amount || 0),
                              0,
                            )
                            .toFixed(2),
                        )}
                      </Text>
                    </Text>
                  </Group>
                </Grid.Col>

                <Grid.Col span={2}>
                  <ScrollArea style={{ height: 350 }} offsetScrollbars>
                    <Table striped highlightOnHover>
                      <thead>
                        <tr>
                          <th style={{ width: '40%' }}>Funcionário</th>
                          <th style={{ width: '25%' }}>Valor</th>
                          <th style={{ width: '30%' }}>Descrição</th>
                          <th style={{ width: '5%' }}></th>
                        </tr>
                      </thead>
                      <tbody>
                        {batchRows.map((row, index) => (
                          <tr key={row.id}>
                            <td>
                              <Autocomplete
                                required
                                placeholder="Nome do funcionário"
                                data={
                                  getUsersResponse?.map((u) => u.username) ?? []
                                }
                                value={row.favoredOrResponsible}
                                onChange={(val) =>
                                  handleUpdateBatchRow(
                                    index,
                                    'favoredOrResponsible',
                                    val,
                                  )
                                }
                              />
                            </td>
                            <td>
                              <NumberInput
                                required
                                placeholder="Valor"
                                decimalSeparator=","
                                precision={2}
                                hideControls
                                value={
                                  row.amount === '' ? undefined : row.amount
                                }
                                onChange={(val) =>
                                  handleUpdateBatchRow(
                                    index,
                                    'amount',
                                    val === undefined ? '' : val,
                                  )
                                }
                              />
                            </td>
                            <td>
                              <TextInput
                                placeholder="Ex: Prêmio de metas"
                                value={row.description}
                                onChange={(e) =>
                                  handleUpdateBatchRow(
                                    index,
                                    'description',
                                    e.target.value,
                                  )
                                }
                              />
                            </td>
                            <td>
                              <Button
                                type="button"
                                color="red"
                                variant="light"
                                compact
                                onClick={() => handleRemoveBatchRow(index)}
                              >
                                <IconTrash size={14} />
                              </Button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  </ScrollArea>
                </Grid.Col>

                <Grid.Col span={2}>
                  <Group position="apart">
                    <Button
                      type="button"
                      variant="outline"
                      onClick={handleAddBatchRow}
                      leftIcon={<IconPlus size={14} />}
                    >
                      Adicionar Linha
                    </Button>

                    <Button type="submit" loading={batchLoading}>
                      Salvar Lançamentos
                    </Button>
                  </Group>
                </Grid.Col>
              </Grid>
            </form>
          </Tabs.Panel>
        </Tabs>
      </Modal>
    </Page>
  );
}
